<template>
  <!-- Error Message -->
  <PageError v-if="error"
   :error="error"
   :errorCode="errorCode"
  />

  <div class="p-6 dark:text-white" v-if="division">
    <h1 class="text-2xl font-semibold mb-6 flex items-center dark:text-white">
      <button
        @click="$router.push('/division', {query: this.tenant.addSchemaParam()})"
        class="mr-4 bg-blue-600 text-white px-4 py-1.5 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition duration-200 text-sm font-medium flex items-center"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="h-4 w-4 mr-1"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fill-rule="evenodd"
            d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
            clip-rule="evenodd"
          />
        </svg>
        Back to Divisions
      </button>
      Division Details: {{ division.name }}
      <div class="relative group">
      <button
        type="button"
        class="hover:bg-transparent bg-transparent ml-2"
        @click="startTutorial"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-circle-question-mark dark:text-gray-50 text-gray-900"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><path d="M12 17h.01"/></svg>
      </button>
        <span class="absolute top-full text-xs right-1/2 -translate-y-1/2 glass-card mb-1 px-1.5 py-0.5 text-xs text-white bg-gray-800 dark:bg-gray-700 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">Tutorial</span>
      </div>
    </h1>
    <!-- Table Header -->
    <div class="overflow-hidden rounded-xl backdrop-blur-md bg-white/80 dark:bg-slate-900/60">
      <table class="w-full text-left">
        <thead class="text-white bg-gradient-to-r from-slate-900/95 to-slate-800/95 dark:from-slate-900/95 dark:to-slate-800/95 backdrop-blur-md border-b border-slate-700/50 dark:border-slate-700/50">
          <tr>
            <th class="py-2 px-4">Field</th>
            <th class="py-2 px-4">Value</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(field, index) in displayFields"
            :key="field.key"
            :id="'division-field-' + field.key"
            :class="['border-b dark:border-gray-700', index % 2 === 0 ? 'hover:bg-gray-900/30 transition-colors' : 'hover:bg-gray-900/30 transition-colors']"
          >
            <td class="py-4 px-4">{{ field.label }}</td>
            <td class="py-4 px-4">
              <div class="flex items-center">
                <template v-if="editingField === field.key">
                  <div class="flex items-start gap-2 w-full">
                    <template v-if="field.type === 'select'">
                      <GlassSelect
                        v-model="editValue"
                        :options="Object.entries(organizations).map(([id, name]) => ({ id: Number(id), name }))"
                        :valueKey="'id'"
                        :labelKey="'name'"
                        placeholder="Select organisation"
                        class="flex-grow"
                      />
                    </template>
                    <template v-else>
                      <div class="flex-grow">
                        <input
                          v-model="editValue"
                          @focus="clearFieldError(field.key)"
                          :type="field.type"
                          @keyup.enter="saveEdit(field.key)"
                          :class="[
                            'block w-full px-3 py-2 text-base border focus:outline-none focus:ring-2 sm:text-sm rounded-md',
                            fieldErrors[field.key]
                              ? 'ring-2 ring-red-500 border-red-500 focus:ring-red-500 focus:border-red-500'
                              : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                          ]"
                        />
                        <FieldError v-if="fieldErrors[field.key]" :message="fieldErrors[field.key]" class="mt-1"/>
                      </div>
                    </template>
                    <div class="flex gap-2 flex-shrink-0">
                      <button
                        type="button"
                        @click.stop="saveEdit(field.key)"
                        @mousedown.prevent
                        class="btn bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 px-3 py-2 rounded"
                      >
                        Save
                      </button>
                      <button
                        type="button"
                        @click.stop="cancelEdit(field.key)"
                        @mousedown.prevent
                        class="btn bg-gray-500 text-white hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 px-3 py-2 rounded"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </template>
                <template v-else>
                  <span class="flex-grow">
                    {{ formatValue(field.key, division[field.key]) }}
                  </span>
                  <div class="relative w-1/2">
                    <button
                    v-if="handleNoEdit(field.key) && (canChange || user?.is_staff)"
                    @click="startEdit(field.key)"
                    class="ml-2 p-1 text-gray-400 hover:text-blue-500 dark:text-blue-500 transition-colors duration-150"
                    @mouseover="showToolTip=true, hoveritem=field.key"
                    @mouseleave="showToolTip=false"
                    >
                    <svg
                      class="h-4 w-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                      />
                    </svg>
                    </button>
                    <span 
                      v-if="showToolTip && hoveritem === field.key"
                      class="absolute bottom-full whitespace-nowrap mb-2 transform -translate-x-1/2 rounded py-1 px-2 dark:border-gray-700 dark:bg-gray-600 bg-gray-800 text-white text-center">
                      Change {{field.label}}
                    </span>
                  </div>
                </template>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
  <div v-else>
    <p>Loading...</p>
  </div>
</template>

<script>
import { getDivisionById, updateDivision } from "@/services/divisionService";
import { getAllOrgs } from "@/services/orgService";
import { useStore } from "@/store";
import FieldError from "@/components/FieldError.vue";
import { useMultiTenant } from "@/composables/useMultiTenant";
import PageError from "@/components/PageError.vue";
import GlassSelect from "@/components/GlassSelect.vue";
import { useTutorial } from "@/composables/tutorial";


export default {
  setup() {
    const tenant = useMultiTenant();
    const { start } = useTutorial();
    return { tenant, _start: start };
  },
  components: {
    PageError,
    FieldError,
    GlassSelect,
  },
  data() {
    return {
      showToolTip: false,
      division: null,
      editingField: null,
      editValue: null,
      originalValue: null,
      noEditFields : ['name', 'last_modified', 'created_date', 'org'],
      displayFields: [
        { key: "name", label: "Name", type: "text" },
        { key: "abbreviated_name", label: "Abbreviated Name", type: "text"},
        { key: "code", label: "Code", type: "text" },
        { key: "org", label: "Organisation", type: "select" },
        { key: "last_modified", label: "Last Modified", type: "text" },
        { key: "created_date", label: "Created Date", type: "text" },
      ],
      canChange: false,
      fieldErrors: {},
      divisionDetailSteps: [
        {
          element: '#division-field-name',
          action: 'display',
          message: "The division's full name."
        },
        {
          element: '#division-field-abbreviated_name',
          action: 'display',
          message: 'The abbreviated name. Click the pencil icon to edit this field.'
        },
        {
          element: '#division-field-code',
          action: 'display',
          message: 'The unique code for this division. Click the pencil icon to edit.'
        },
        {
          element: '#division-field-org',
          action: 'display',
          message: 'The organisation this division belongs to.'
        },
        {
          element: '#division-field-last_modified',
          action: 'display',
          message: 'Timestamp of the last change made to this division.'
        },
      ],
    };
  },
  async mounted() {
    const divisionId = this.$route.params.id;
    const validation = this.tenant.validateSchemaForOperation();
    try {
      this.checkPermissions();
      await this.tenant.fetchOrganizations();
      this.division = await getDivisionById(divisionId, validation.schemaName);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  },
  computed: {
    user(){
      return useStore.getUser();
    },
  },
  watch: {
    user: {
      handler(user) {
        if (user) {
          this.checkPermissions();
        }
      },
      immediate: true
    },
  },
  methods: {
    async startTutorial() {
      await this._start(this.divisionDetailSteps);
    },
    clearFieldError(fieldName){
      if (this.fieldErrors[fieldName]) {
        delete this.fieldErrors[fieldName];
        delete this.fieldErrors['non_field_errors'];
      }
    },
    async checkPermissions() {
      if (this.user) {
        this.canChange = await useStore.hasPermission("Can change division");
      }
    },
    startEdit(key) {
      if (this.canChange || this.user?.is_staff){
        this.editingField = key;
        this.editValue = this.division[key];
        this.originalValue = this.editValue
        this.showToolTip = false;
      }
    },
    cancelEdit(key) {
      this.clearFieldError(key);
      this.editingField = null;
      this.editValue = null;
      this.division[key] = this.originalValue;
    },
    handleNoEdit(key){
      return !this.noEditFields.includes(key);
    },
    validateDivisionData(key){
      if (this.division[key] === ''){
        this.fieldErrors[key] = "This field cannot be empty."
      }
    },
    async saveEdit(key) {
      try {
        const validation = this.tenant.validateSchemaForOperation();
        if (!validation.valid) {
          this.error = "Malformed request. Failed to update division!";
          this.errorCode = "422!";
          return;
        }
        this.division[key] = this.editValue;

        this.validateDivisionData(key);
        if (Object.keys(this.fieldErrors).length > 0) return;

        const updatedDivision = await updateDivision(
          this.division.id,
          this.division,
          validation.schemaName
        );
        this.division = { ...this.division, ...updatedDivision };
        this.editingField = null;
        this.editValue = null;
      } catch (error) {
        this.fieldErrors = {}; 
        const items = this.displayFields;
        const errData = error?.response?.data;

        items.forEach(element => {
          const fieldError = errData?.[element.key];
          if (fieldError?.[0]) {
            this.fieldErrors[element.key] = fieldError[0]; 
          }
        });
        console.error("Error updating division:", error);
      }
    },
    formatValue(key, value) {
      if (key === "org") {
        return this.tenant.getOrganizationName(value) || "redacted";
      }
      if (key === "last_modified" || key === "created_date") {
        return this.formatDateTime(value);
      }
      return value !== null && value !== undefined ? value : "N/A";
    },
    formatDateTime(dateString) {
      const options = {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        timeZoneName: "short",
      };
      return new Date(dateString).toLocaleString(undefined, options);
    },
    getPendingValue(key) {
      // This is a placeholder - implement pending value logic if needed
      return 'N/A';
    },
  },
};
</script>
